import { useState } from "react";
import { ArrowLeftRight, Check, Gift, Ship, Swords, UserPlus } from "lucide-react";
import { Face } from "@/city3d/Hud";
import { pointOf } from "./CoinFly";
import { Btn, Info, Pill, Pop, Ring } from "./kit";
import { ACC, dur, fmt, tradeBlock, useNow } from "./util";

// A pale tile in its category's tint; `fill` makes it the one thing to do right now.
const Tile = ({ icon: Icon, label, sub, badge, acc, fill, testId, ...p }) => (
  <button type="button" data-testid={testId} {...p} style={{ "--acc": ACC[acc] }}
    className={`accent-btn ${fill ? "fill" : ""} relative flex h-[68px] min-w-[72px] flex-col items-center justify-center gap-0.5 rounded-[20px] px-3 sm:h-[74px] sm:min-w-[92px]`}>
    <Icon className="h-6 w-6" strokeWidth={2.2} />
    <span className="text-[13px] font-semibold leading-none">{label}</span>
    {sub != null && <span className="text-[11px] font-medium leading-none tabular-nums">{sub}</span>}
    {badge ? <span key={badge} className="badge-pop absolute -right-2 -top-2 z-10 min-w-[22px] rounded-full bg-x-neg px-1 text-center text-[11px] font-semibold leading-[22px] tabular-nums text-white ring-2 ring-white">{badge}</span> : null}
  </button>
);

export function ActionDock({ econ, clockOffset, meK, className = "", onCollect, onOpen, onTrade, onInvite }) {
  const now = useNow(clockOffset);
  const [pop, setPop] = useState(false);
  const docked = econ?.berths.filter((b) => b.ship) ?? [];
  const ready = docked.filter((b) => Date.parse(b.done) <= now);
  const free = econ && docked.length < econ.berths.length;
  const next = docked.length > ready.length && Math.min(...docked.filter((b) => !ready.includes(b)).map((b) => Date.parse(b.done)));
  const v = econ?.visits;

  return (
    <div className={`pointer-events-none absolute inset-x-0 z-10 flex justify-center ${className}`} style={{ bottom: "calc(3rem + env(safe-area-inset-bottom))" }}>
      <div className="pointer-events-auto relative flex gap-2 sm:gap-3" data-testid="action-dock">
        {ready.length ? (
          <Tile acc="blue" fill icon={Ship} label="Collect" sub={`+${fmt(ready.reduce((s, b) => s + b.pay, 0))}`} badge={ready.length} testId="action-ships"
            onClick={(e) => onCollect(pointOf(e.currentTarget))} />
        ) : (
          <Tile acc="blue" icon={Ship} label="Ships" testId="action-ships" onClick={() => onOpen("economy", "dock")}
            sub={free ? "Free" : next ? dur(next - now) : "–"} />
        )}
        <Tile acc="rose" icon={Swords} label="Duel" testId="action-duel" badge={econ?.duels.inbox} onClick={() => onOpen("economy", "duels")}
          sub={econ?.duels.free ? "Free" : null} />
        <Tile acc="green" icon={ArrowLeftRight} label="Trade" testId="action-visit" data-pop sub={v ? `${v.left}/${v.of}` : null} onClick={() => setPop(!pop)} />
        <Tile acc="grey" icon={UserPlus} label="Invite" testId="action-invite" onClick={onInvite} />

        {pop && v && (
          <div className="absolute bottom-full left-1/2 mb-3 flex w-0 justify-center">
            <Pop onClose={() => setPop(false)} className="w-[min(92vw,330px)] shrink-0">
              <div className="flex items-center gap-2.5 px-2 pb-2 pt-1">
                <Ring value={v.left / v.of} size={46} stroke={4} acc="green"><b className="text-base tabular-nums">{v.left}</b></Ring>
                <div className="flex flex-1 flex-wrap gap-1" title="Mystery crate prizes">
                  {(v.crate?.prizes ?? []).map((n, i) => <Pill key={n} acc="gold"><Gift className="h-3 w-3" />{n}</Pill>)}
                </div>
                <Info text={`Trade with a dock: it boosts their ships and opens a mystery crate for you. ${v.of} trades a day, one per dock.`} />
              </div>
              <ul className="hud-scroll max-h-[clamp(8rem,calc(100vh_-_400px),16rem)] space-y-1 overflow-y-auto">
                {v.suggest.map((p) => {
                  const why = tradeBlock(p, econ, meK);
                  return (
                    <li key={p.k} className="flex items-center gap-2 rounded-[10px] px-2 py-1.5 hover:bg-x-raised">
                      <Face p={p} size={30} ring={ACC.green} />
                      <b className="min-w-0 flex-1 truncate text-sm">@{p.h}</b>
                      <span className="text-xs font-medium tabular-nums text-x-pos">+{fmt(p.wk)}</span>
                      <Btn tone="green" disabled={Boolean(why)} title={why ?? "Trade"} data-testid="visit-button" onClick={(e) => onTrade(p, pointOf(e.currentTarget))}>{why ? <Check className="h-4 w-4" /> : "Trade"}</Btn>
                    </li>
                  );
                })}
                {!v.suggest.length && <li className="px-2 py-4 text-center text-sm text-x-mute">No docks to trade with right now.</li>}
              </ul>
            </Pop>
          </div>
        )}
      </div>
    </div>
  );
}
